import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _excluded = ["notification", "listIndex"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useContext, useMemo } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { MarkRequestReadState, RequestCategory } from '@atlassian/notification-types/types';
import { NotificationsStoreContext } from '../../ui/notifications-context';
import { getCrossAttributes, getDefaultAttributes, getNotificationAttributes, getNotificationContainer, getNotificationGroupAttributes, getRenderBucket, triggerOperationalEvent, triggerScreenEvent, triggerTrackEvent, triggerUIEvent } from './utils';

/**
 * SCREEN events
 */

export var triggerNotificationFilterViewed = function triggerNotificationFilterViewed(createAnalyticsEvent, analyticsContext) {
  return function (firstRender, firstFilterView) {
    triggerScreenEvent(createAnalyticsEvent, {
      name: analyticsContext.screenName,
      attributes: _objectSpread({
        firstFilterView: firstFilterView.toString(),
        firstRender: firstRender.toString()
      }, getDefaultAttributes(analyticsContext))
    }, analyticsContext);
  };
};
export var triggerShortcutsDialogViewed = function triggerShortcutsDialogViewed(createAnalyticsEvent, analyticsContext) {
  return function () {
    triggerScreenEvent(createAnalyticsEvent, {
      name: 'notificationsKeyboardShortcutsDialog',
      attributes: _objectSpread({}, getDefaultAttributes(analyticsContext))
    }, analyticsContext);
  };
};
export var triggerRequestNotificationViewed = function triggerRequestNotificationViewed(createAnalyticsEvent, analyticsContext) {
  return function () {
    triggerScreenEvent(createAnalyticsEvent, {
      name: 'requestNotification',
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        categoryFilter: RequestCategory.DIRECT
      })
    }, analyticsContext);
  };
};

/**
 * UI events
 */

export var triggerHeaderDropdownMenuClicked = function triggerHeaderDropdownMenuClicked(createAnalyticsEvent, analyticsContext) {
  return function (menuState) {
    triggerUIEvent(createAnalyticsEvent, {
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'dropdownMenu',
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        menuState: menuState
      })
    }, analyticsContext);
  };
};
export var triggerNotificationLinkClicked = function triggerNotificationLinkClicked(createAnalyticsEvent, analyticsContext) {
  return function (notification, listIndex, linkType, clickType) {
    triggerUIEvent(createAnalyticsEvent, _objectSpread({
      action: 'clicked',
      actionSubject: 'link',
      actionSubjectId: 'notificationItem',
      attributes: _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, getCrossAttributes(notification, analyticsContext)), getDefaultAttributes(analyticsContext)), getNotificationAttributes(notification, listIndex)), {}, {
        clickType: clickType,
        linkType: linkType
      })
    }, getNotificationContainer(notification)), analyticsContext);

    /**
     * @Deprecated please remove from 01/07/2023
     * https://product-fabric.atlassian.net/browse/NP-5681
     */
    triggerUIEvent(createAnalyticsEvent, _objectSpread({
      action: 'clicked',
      actionSubject: 'link',
      actionSubjectId: linkType,
      attributes: _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, getCrossAttributes(notification, analyticsContext)), getDefaultAttributes(analyticsContext)), getNotificationAttributes(notification, listIndex)), {}, {
        clickType: clickType
      })
    }, getNotificationContainer(notification)), analyticsContext);
  };
};
export var triggerConfluenceCommentReplyButtonClicked = function triggerConfluenceCommentReplyButtonClicked(createAnalyticsEvent, analyticsContext) {
  return function (notification, listIndex) {
    triggerUIEvent(createAnalyticsEvent, _objectSpread({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'replyToComment',
      attributes: _objectSpread(_objectSpread(_objectSpread({}, getCrossAttributes(notification, analyticsContext)), getDefaultAttributes(analyticsContext)), getNotificationAttributes(notification, listIndex))
    }, getNotificationContainer(notification)), analyticsContext);
  };
};
export var triggerJiraReactionLoadedAnalytics = function triggerJiraReactionLoadedAnalytics(createAnalyticsEvent, analyticsContext) {
  return function (_ref) {
    var notification = _ref.notification,
      listIndex = _ref.listIndex,
      commentId = _ref.commentId,
      issueId = _ref.issueId,
      projectId = _ref.projectId,
      projectType = _ref.projectType;
    triggerUIEvent(createAnalyticsEvent, _objectSpread({
      action: 'loaded',
      actionSubject: 'reactionBar',
      attributes: _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, getCrossAttributes(notification, analyticsContext)), getDefaultAttributes(analyticsContext)), getNotificationAttributes(notification, listIndex)), {}, {
        commentId: commentId,
        issueId: issueId,
        isFromNotificationDraw: true,
        projectType: projectType,
        projectId: projectId
      })
    }, getNotificationContainer(notification)), analyticsContext);
  };
};
export var triggerJiraIssueCommentLoadedAnalytics = function triggerJiraIssueCommentLoadedAnalytics(createAnalyticsEvent, analyticsContext) {
  return function (_ref2) {
    var notification = _ref2.notification,
      listIndex = _ref2.listIndex,
      rest = _objectWithoutProperties(_ref2, _excluded);
    triggerUIEvent(createAnalyticsEvent, _objectSpread({
      action: 'issueDataLoaded',
      actionSubject: 'reactionBar',
      attributes: _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, getCrossAttributes(notification, analyticsContext)), getDefaultAttributes(analyticsContext)), getNotificationAttributes(notification, listIndex)), {}, {
        isFromNotificationDraw: true
      }, rest)
    }, getNotificationContainer(notification)), analyticsContext);
  };
};
export var triggerJiraReactionsUpdatedAnalytics = function triggerJiraReactionsUpdatedAnalytics(createAnalyticsEvent, analyticsContext) {
  return function (_ref3) {
    var notification = _ref3.notification,
      listIndex = _ref3.listIndex,
      commentId = _ref3.commentId,
      issueId = _ref3.issueId,
      emojiId = _ref3.emojiId,
      action = _ref3.action,
      reactionCountHierarchy = _ref3.reactionCountHierarchy,
      projectId = _ref3.projectId,
      projectType = _ref3.projectType;
    triggerTrackEvent(createAnalyticsEvent, _objectSpread({
      action: action,
      actionSubject: 'reaction',
      actionSubjectId: emojiId,
      attributes: _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, getCrossAttributes(notification, analyticsContext)), getDefaultAttributes(analyticsContext)), getNotificationAttributes(notification, listIndex)), {}, {
        commentId: commentId,
        issueId: issueId,
        isFromNotificationDraw: true,
        reactionCountHierarchy: reactionCountHierarchy,
        projectType: projectType,
        projectId: projectId
      })
    }, getNotificationContainer(notification)), analyticsContext);
  };
};
export var triggerReactionCreatedInContext = function triggerReactionCreatedInContext(createAnalyticsEvent, analyticsContext) {
  return function (notification, listIndex, contentId, containerId, containerType, emojiId, userId) {
    triggerUIEvent(createAnalyticsEvent, _objectSpread({
      action: 'created',
      actionSubject: 'reaction',
      attributes: _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, getCrossAttributes(notification, analyticsContext)), getDefaultAttributes(analyticsContext)), getNotificationAttributes(notification, listIndex)), {}, {
        objectId: contentId,
        objectType: 'comment',
        containerId: containerId,
        containerType: containerType,
        emojiId: emojiId,
        accountId: userId,
        displayType: 'notification-bell-drawer-comment'
      })
    }, getNotificationContainer(notification)), analyticsContext);
  };
};
export var triggerReactionDeletedInContext = function triggerReactionDeletedInContext(createAnalyticsEvent, analyticsContext) {
  return function (notification, listIndex, contentId, containerId, containerType, emojiId, userId) {
    triggerUIEvent(createAnalyticsEvent, _objectSpread({
      action: 'deleted',
      actionSubject: 'reaction',
      attributes: _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, getCrossAttributes(notification, analyticsContext)), getDefaultAttributes(analyticsContext)), getNotificationAttributes(notification, listIndex)), {}, {
        objectId: contentId,
        objectType: 'comment',
        containerId: containerId,
        containerType: containerType,
        emojiId: emojiId,
        accountId: userId,
        displayType: 'notification-bell-drawer-comment'
      })
    }, getNotificationContainer(notification)), analyticsContext);
  };
};
export var triggerDocumentExpanded = function triggerDocumentExpanded(createAnalyticsEvent, analyticsContext) {
  return function (notification, listIndex) {
    triggerUIEvent(createAnalyticsEvent, _objectSpread({
      action: 'expanded',
      actionSubject: 'notification',
      actionSubjectId: 'document',
      attributes: _objectSpread(_objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), getNotificationAttributes(notification, listIndex)), {}, {
        feature: 'expand'
      })
    }, getNotificationContainer(notification)), analyticsContext);
  };
};
export var triggerDocumentCollapsed = function triggerDocumentCollapsed(createAnalyticsEvent, analyticsContext) {
  return function (notification, listIndex) {
    triggerUIEvent(createAnalyticsEvent, _objectSpread({
      action: 'collapsed',
      actionSubject: 'notification',
      actionSubjectId: 'document',
      attributes: _objectSpread(_objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), getNotificationAttributes(notification, listIndex)), {}, {
        feature: 'expand'
      })
    }, getNotificationContainer(notification)), analyticsContext);
  };
};
export var triggerNotificationGroupExpanded = function triggerNotificationGroupExpanded(createAnalyticsEvent, analyticsContext) {
  return function (action, group, listIndex) {
    triggerUIEvent(createAnalyticsEvent, {
      action: action,
      actionSubject: 'notification',
      actionSubjectId: 'group',
      attributes: _objectSpread(_objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), getNotificationGroupAttributes(group, listIndex)), {}, {
        objectType: 'notificationGroup',
        objectId: group.id,
        groupSize: group.size
      })
    }, analyticsContext);
  };
};
export var triggerNotificationsViewToggled = function triggerNotificationsViewToggled(createAnalyticsEvent, analyticsContext) {
  return function (toView) {
    triggerUIEvent(createAnalyticsEvent, {
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'toggleNotificationsView',
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        toView: toView
      })
    }, analyticsContext);
  };
};
export var triggerNotificationsGiveFeedback = function triggerNotificationsGiveFeedback(createAnalyticsEvent, analyticsContext) {
  return function () {
    triggerUIEvent(createAnalyticsEvent, {
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'giveFeedback',
      attributes: _objectSpread({}, getDefaultAttributes(analyticsContext))
    }, analyticsContext);
  };
};
export var triggerGroupingOnboardingAction = function triggerGroupingOnboardingAction(createAnalyticsEvent, analyticsContext) {
  return function (actionSubjectId, step) {
    triggerUIEvent(createAnalyticsEvent, {
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: actionSubjectId,
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        step: step
      })
    }, analyticsContext);
  };
};
export var triggerViewedAllNotificationsUIEvent = function triggerViewedAllNotificationsUIEvent(createAnalyticsEvent, analyticsContext) {
  return function (viewedCount) {
    triggerUIEvent(createAnalyticsEvent, {
      action: 'viewed',
      actionSubject: 'message',
      actionSubjectId: 'allNotificationsViewed',
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        viewedCount: viewedCount
      })
    }, analyticsContext);
  };
};
export var triggerFPNLinkClicked = function triggerFPNLinkClicked(createAnalyticsEvent, analyticsContext) {
  return function () {
    triggerUIEvent(createAnalyticsEvent, {
      action: 'clicked',
      actionSubject: 'link',
      actionSubjectId: 'fullPageNotifications',
      attributes: _objectSpread({}, getDefaultAttributes(analyticsContext))
    }, analyticsContext);
  };
};
export var triggerRequestTabClicked = function triggerRequestTabClicked(createAnalyticsEvent, analyticsContext) {
  return function (categoryFilter, inTabsStickyHeader) {
    triggerUIEvent(createAnalyticsEvent, {
      action: 'clicked',
      actionSubject: 'tab',
      actionSubjectId: 'notificationTab',
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        categoryFilter: categoryFilter,
        inTabsStickyHeader: inTabsStickyHeader
      })
    }, analyticsContext);
  };
};
export var triggerActionCtaButtonClicked = function triggerActionCtaButtonClicked(createAnalyticsEvent, analyticsContext) {
  return function (actionAppearance, notification, index) {
    triggerUIEvent(createAnalyticsEvent, _objectSpread({
      action: 'clicked',
      actionSubject: 'link',
      actionSubjectId: actionAppearance.toLocaleLowerCase() + 'Action',
      attributes: _objectSpread(_objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), getNotificationAttributes(notification)), {}, {
        actionButtonIndex: index
      })
    }, getNotificationContainer(notification)), analyticsContext);
  };
};
export var triggerNotificationProductFilterClicked = function triggerNotificationProductFilterClicked(createAnalyticsEvent, analyticsContext) {
  return function (selectedProduct) {
    triggerUIEvent(createAnalyticsEvent, {
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'fullPageNotificationsProductFilter',
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        productFilter: selectedProduct
      })
    }, analyticsContext);
  };
};
export var triggerNotificationCategoryFilterClicked = function triggerNotificationCategoryFilterClicked(createAnalyticsEvent, analyticsContext) {
  return function (selectedCategory) {
    triggerUIEvent(createAnalyticsEvent, {
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'fullPageNotificationsCategoryFilter',
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        categoryFilter: selectedCategory
      })
    }, analyticsContext);
  };
};
export var triggerNotificationFilterClicked = function triggerNotificationFilterClicked(createAnalyticsEvent, analyticsContext) {
  return function (notificationFilterType) {
    triggerUIEvent(createAnalyticsEvent, {
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'fullPageNotificationsFilter',
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), notificationFilterType)
    }, analyticsContext);
  };
};
export var triggerNotificationSearchClicked = function triggerNotificationSearchClicked(createAnalyticsEvent, analyticsContext) {
  return function () {
    triggerUIEvent(createAnalyticsEvent, {
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'fullPageNotificationsSearch',
      attributes: _objectSpread({}, getDefaultAttributes(analyticsContext))
    }, analyticsContext);
  };
};

/**
 * Track events
 */

export var triggerNotificationViewedTrackEvent = function triggerNotificationViewedTrackEvent(createAnalyticsEvent, analyticsContext) {
  return function (notification, listIndex, notificationViewedDuration) {
    triggerTrackEvent(createAnalyticsEvent, _objectSpread({
      action: 'viewed',
      actionSubject: 'notification',
      attributes: _objectSpread(_objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), getNotificationAttributes(notification, listIndex)), {}, {
        viewedDuration: notificationViewedDuration,
        viewed_duration: notificationViewedDuration
      })
    }, getNotificationContainer(notification)), analyticsContext);
  };
};
export var triggerFullPageNotificationViewedTrackEvent = function triggerFullPageNotificationViewedTrackEvent(createAnalyticsEvent, analyticsContext) {
  return function (fullPageNotificationViewedDuration) {
    triggerTrackEvent(createAnalyticsEvent, {
      action: 'viewed',
      actionSubject: 'page',
      actionSubjectId: 'fullPageNotifications',
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        viewedDuration: fullPageNotificationViewedDuration
      })
    }, analyticsContext);
  };
};
export var triggerMarkAllReadButtonClicked = function triggerMarkAllReadButtonClicked(createAnalyticsEvent, analyticsContext) {
  return function (triggeredFrom) {
    triggerTrackEvent(createAnalyticsEvent, {
      action: 'marked',
      actionSubject: 'notification',
      actionSubjectId: 'all',
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        toState: MarkRequestReadState.READ.toLocaleLowerCase(),
        markType: 'all',
        triggeredFrom: triggeredFrom
      })
    }, analyticsContext);
  };
};
export var triggerMarkOneReadButtonClicked = function triggerMarkOneReadButtonClicked(createAnalyticsEvent, analyticsContext) {
  return function (toReadState, notification, listIndex) {
    triggerTrackEvent(createAnalyticsEvent, _objectSpread({
      action: 'marked',
      actionSubject: 'notification',
      actionSubjectId: 'single',
      attributes: _objectSpread(_objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), getNotificationAttributes(notification, listIndex)), {}, {
        toState: toReadState.toLocaleLowerCase(),
        markType: 'single'
      })
    }, getNotificationContainer(notification)), analyticsContext);
  };
};
export var triggerMarkNotificationGroup = function triggerMarkNotificationGroup(createAnalyticsEvent, analyticsContext) {
  return function (toReadState, group, listIndex) {
    triggerTrackEvent(createAnalyticsEvent, {
      action: 'marked',
      actionSubject: 'notification',
      actionSubjectId: 'group',
      attributes: _objectSpread(_objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), getNotificationGroupAttributes(group, listIndex)), {}, {
        toState: toReadState.toLocaleLowerCase(),
        markType: 'group',
        groupSize: group.size,
        objectId: group.id,
        objectType: 'notificationGroup'
      })
    }, analyticsContext);
  };
};
export var triggerKeyboardShortcutUsed = function triggerKeyboardShortcutUsed(createAnalyticsEvent, analyticsContext) {
  return function (shortcut, notification, listIndex) {
    triggerUIEvent(createAnalyticsEvent, _objectSpread({
      action: 'pressed',
      actionSubject: 'keyboardShortcut',
      actionSubjectId: "".concat(shortcut, "KeyboardShortcut"),
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), getNotificationAttributes(notification, listIndex))
    }, getNotificationContainer(notification)), analyticsContext);
  };
};
export var triggerConfluenceCommentReplyCreated = function triggerConfluenceCommentReplyCreated(createAnalyticsEvent, analyticsContext) {
  return function (notification, listIndex, commentId, commentType, containerId, containerType, parentCommentId) {
    triggerTrackEvent(createAnalyticsEvent, _objectSpread({
      action: 'created',
      actionSubject: 'comment',
      actionSubjectId: commentId,
      attributes: _objectSpread(_objectSpread(_objectSpread({
        objectType: containerType,
        objectId: containerId,
        commentType: commentType,
        parentCommentId: parentCommentId
      }, getCrossAttributes(notification, analyticsContext)), getDefaultAttributes(analyticsContext)), getNotificationAttributes(notification, listIndex))
    }, getNotificationContainer(notification)), analyticsContext);
  };
};
export var triggerJiraCommentReplyButtonClicked = function triggerJiraCommentReplyButtonClicked(createAnalyticsEvent, analyticsContext) {
  return function (notification, listIndex, commentSessionId) {
    triggerUIEvent(createAnalyticsEvent, _objectSpread({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'replyToJiraComment',
      attributes: _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, getCrossAttributes(notification, analyticsContext)), getDefaultAttributes(analyticsContext)), getNotificationAttributes(notification, listIndex)), {}, {
        commentSessionId: commentSessionId
      })
    }, getNotificationContainer(notification)), analyticsContext);
  };
};
export var triggerJiraCommentReplyCreated = function triggerJiraCommentReplyCreated(createAnalyticsEvent, analyticsContext) {
  return function (notification, listIndex, commentId, parentCommentId, containerId, containerType, commentSessionId, projectType, projectId, mentionedUserIds) {
    triggerTrackEvent(createAnalyticsEvent, _objectSpread({
      objectType: containerType,
      objectId: containerId,
      action: 'created',
      actionSubject: 'comment',
      actionSubjectId: commentId,
      projectType: projectType,
      projectId: projectId,
      attributes: _objectSpread(_objectSpread(_objectSpread(_objectSpread({
        isReply: true,
        parentCommentId: parentCommentId,
        isFromNotificationDraw: true,
        commentSessionId: commentSessionId
      }, mentionedUserIds && mentionedUserIds.length > 0 && {
        mentionedUserIds: mentionedUserIds
      }), getCrossAttributes(notification, analyticsContext)), getDefaultAttributes(analyticsContext)), getNotificationAttributes(notification, listIndex))
    }, getNotificationContainer(notification)), analyticsContext);
  };
};
export var triggerJiraCommentReplyCancelled = function triggerJiraCommentReplyCancelled(createAnalyticsEvent, analyticsContext) {
  return function (notification, listIndex, commentSessionId) {
    triggerUIEvent(createAnalyticsEvent, _objectSpread({
      action: 'cancelled',
      actionSubject: 'button',
      actionSubjectId: 'replyToJiraComment',
      attributes: _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, getCrossAttributes(notification, analyticsContext)), getDefaultAttributes(analyticsContext)), getNotificationAttributes(notification, listIndex)), {}, {
        commentSessionId: commentSessionId
      })
    }, getNotificationContainer(notification)), analyticsContext);
  };
};

/**
 * Trigger operational events
 */

export var triggerErrorBoundaryRenderedEvent = function triggerErrorBoundaryRenderedEvent(createAnalyticsEvent, analyticsContext) {
  return function (errorBoundaryType) {
    var isCritical = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    var notificationContext = arguments.length > 2 ? arguments[2] : undefined;
    var listIndex = arguments.length > 3 ? arguments[3] : undefined;
    triggerOperationalEvent(createAnalyticsEvent, _objectSpread({
      action: 'rendered',
      actionSubject: 'errorBoundary',
      actionSubjectId: 'notifications',
      attributes: _objectSpread(_objectSpread({
        errorBoundaryType: errorBoundaryType,
        isCritical: isCritical
      }, getDefaultAttributes(analyticsContext)), notificationContext && listIndex !== undefined && getNotificationAttributes(notificationContext, listIndex))
    }, notificationContext && listIndex !== undefined && getNotificationContainer(notificationContext)), analyticsContext);
  };
};
export var triggerFailedToRetrieveNotificationsEvent = function triggerFailedToRetrieveNotificationsEvent(createAnalyticsEvent, analyticsContext) {
  return function (notificationsRendered) {
    triggerOperationalEvent(createAnalyticsEvent, {
      action: 'rendered',
      actionSubject: 'errorBoundary',
      actionSubjectId: 'notificationRetrieval',
      attributes: _objectSpread({
        errorBoundaryType: 'notificationRetrieval',
        notificationsRendered: notificationsRendered
      }, getDefaultAttributes(analyticsContext))
    }, analyticsContext);
  };
};
export var triggerNotificationsAPICallSucceeded = function triggerNotificationsAPICallSucceeded(createAnalyticsEvent, analyticsContext) {
  return function (route) {
    var additionalAttributes = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    triggerOperationalEvent(createAnalyticsEvent, {
      action: 'succeeded',
      actionSubject: 'notificationsAPI',
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        route: route
      }, additionalAttributes)
    }, analyticsContext);
  };
};
export var triggerNotificationsAPICallFailed = function triggerNotificationsAPICallFailed(createAnalyticsEvent, analyticsContext) {
  return function (route, statusCode) {
    var additionalAttributes = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    triggerOperationalEvent(createAnalyticsEvent, {
      action: 'failed',
      actionSubject: 'notificationsAPI',
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        route: route,
        statusCode: statusCode
      }, additionalAttributes)
    }, analyticsContext);
  };
};
export var triggerAvailableActorsAPICallSucceeded = function triggerAvailableActorsAPICallSucceeded(createAnalyticsEvent, analyticsContext) {
  return function (route) {
    var additionalAttributes = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    triggerOperationalEvent(createAnalyticsEvent, {
      action: 'succeeded',
      actionSubject: 'availableActors',
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        route: route
      }, additionalAttributes)
    }, analyticsContext);
  };
};
export var triggerAvailableActorsAPICallFailed = function triggerAvailableActorsAPICallFailed(createAnalyticsEvent, analyticsContext) {
  return function (route, statusCode) {
    var additionalAttributes = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    triggerOperationalEvent(createAnalyticsEvent, {
      action: 'failed',
      actionSubject: 'availableActors',
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        route: route,
        statusCode: statusCode
      }, additionalAttributes)
    }, analyticsContext);
  };
};
export var triggerAvailableProductsAPICallSucceeded = function triggerAvailableProductsAPICallSucceeded(createAnalyticsEvent, analyticsContext) {
  return function (route) {
    var additionalAttributes = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    triggerOperationalEvent(createAnalyticsEvent, {
      action: 'succeeded',
      actionSubject: 'aisAPI',
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        route: route
      }, additionalAttributes)
    }, analyticsContext);
  };
};
export var triggerAvailableProductsAPICallFailed = function triggerAvailableProductsAPICallFailed(createAnalyticsEvent, analyticsContext) {
  return function (route, statusCode) {
    var additionalAttributes = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    triggerOperationalEvent(createAnalyticsEvent, {
      action: 'failed',
      actionSubject: 'aisAPI',
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        route: route,
        statusCode: statusCode
      }, additionalAttributes)
    }, analyticsContext);
  };
};
export var triggerNotificationsLoaded = function triggerNotificationsLoaded(createAnalyticsEvent, analyticsContext) {
  return function (firstLoad, withContent, duration, limit) {
    var route = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : '/notifications';
    var actionSubject = withContent ? 'notificationsWithContent' : 'notificationsWithoutContent';
    triggerOperationalEvent(createAnalyticsEvent, {
      action: 'fetched',
      actionSubject: actionSubject,
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        firstLoad: firstLoad,
        duration: duration,
        bucket: getRenderBucket(duration, withContent ? withContentBuckets : withoutContentBuckets),
        limit: limit,
        route: route
      })
    }, analyticsContext);
  };
};
export var triggerNotificationsFailedToLoad = function triggerNotificationsFailedToLoad(createAnalyticsEvent, analyticsContext) {
  return function (firstLoad, withContent, limit, statusCode) {
    var route = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : '/notifications';
    var actionSubject = withContent ? 'notificationsWithContent' : 'notificationsWithoutContent';
    triggerOperationalEvent(createAnalyticsEvent, {
      action: 'failedFetch',
      actionSubject: actionSubject,
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        firstLoad: firstLoad,
        limit: limit,
        statusCode: statusCode,
        route: route
      })
    }, analyticsContext);
  };
};
export var triggerFetchConfluenceCommentDataInContextSucceeded = function triggerFetchConfluenceCommentDataInContextSucceeded(createAnalyticsEvent, analyticsContext) {
  return function (name, commentId, duration) {
    triggerOperationalEvent(createAnalyticsEvent, {
      action: 'succeeded',
      actionSubject: 'commentData',
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        name: name,
        displayType: 'notification-bell-drawer-comment',
        commentId: commentId,
        duration: duration
      })
    }, analyticsContext);
  };
};
export var triggerFetchConfluenceCommentDataInContextFailed = function triggerFetchConfluenceCommentDataInContextFailed(createAnalyticsEvent, analyticsContext) {
  return function (name, commentId, duration, reason) {
    triggerOperationalEvent(createAnalyticsEvent, {
      action: 'failed',
      actionSubject: 'commentData',
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        name: name,
        displayType: 'notification-bell-drawer-comment',
        commentId: commentId,
        duration: duration,
        reason: reason
      })
    }, analyticsContext);
  };
};
export var triggerFetchConfluenceCommentDataInContextAborted = function triggerFetchConfluenceCommentDataInContextAborted(createAnalyticsEvent, analyticsContext) {
  return function (name, duration, reason) {
    triggerOperationalEvent(createAnalyticsEvent, {
      action: 'aborted',
      actionSubject: 'commentData',
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        name: name,
        displayType: 'notification-bell-drawer-comment',
        duration: duration,
        reason: reason
      })
    }, analyticsContext);
  };
};
export var triggerCommentReplyInContextSucceeded = function triggerCommentReplyInContextSucceeded(createAnalyticsEvent, analyticsContext) {
  return function (name, mutationType, containerType, duration) {
    triggerOperationalEvent(createAnalyticsEvent, {
      action: 'succeeded',
      actionSubject: 'commentReply',
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        name: name,
        displayType: 'notification-bell-drawer-comment',
        containerType: containerType,
        mutationType: mutationType,
        duration: duration
      })
    }, analyticsContext);
  };
};
export var triggerCommentReplyInContextFailed = function triggerCommentReplyInContextFailed(createAnalyticsEvent, analyticsContext) {
  return function (name, mutationType, containerType, duration, reason) {
    triggerOperationalEvent(createAnalyticsEvent, {
      action: 'failed',
      actionSubject: 'commentReply',
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        name: name,
        displayType: 'notification-bell-drawer-comment',
        containerType: containerType,
        mutationType: mutationType,
        duration: duration,
        reason: reason
      })
    }, analyticsContext);
  };
};
export var triggerCommentReplyInContextAborted = function triggerCommentReplyInContextAborted(createAnalyticsEvent, analyticsContext) {
  return function (name, duration, reason) {
    triggerOperationalEvent(createAnalyticsEvent, {
      action: 'aborted',
      actionSubject: 'commentReply',
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        name: name,
        displayType: 'notification-bell-drawer-comment',
        duration: duration,
        reason: reason
      })
    }, analyticsContext);
  };
};
export var triggerI18nFailedToLoad = function triggerI18nFailedToLoad(createAnalyticsEvent, analyticsContext) {
  return function (locale) {
    triggerOperationalEvent(createAnalyticsEvent, {
      action: 'fetchFailed',
      actionSubject: 'i18nMessages',
      attributes: {
        locale: locale
      }
    }, analyticsContext);
  };
};
var withoutContentBuckets = ['8000', '6000', '4000', '3000', '2500', '2000', '1000', '500'];
var withContentBuckets = ['15000', '10000', '8000', '6000', '4000', '2000', '1000'];
export var triggerNotificationsRenderSucceeded = function triggerNotificationsRenderSucceeded(createAnalyticsEvent, analyticsContext) {
  return function (firstLoad, withContent, duration, noNotificationsScreen) {
    var actionSubject = withContent ? 'notificationsWithContent' : 'notificationsWithoutContent';
    var buckets = withContent ? withContentBuckets : withoutContentBuckets;
    triggerOperationalEvent(createAnalyticsEvent, {
      action: 'renderSucceeded',
      actionSubject: actionSubject,
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        firstLoad: firstLoad,
        duration: duration,
        bucket: getRenderBucket(duration, buckets),
        noNotificationsScreen: noNotificationsScreen
      })
    }, analyticsContext);
  };
};
export var triggerNotificationsRenderFailed = function triggerNotificationsRenderFailed(createAnalyticsEvent, analyticsContext) {
  return function (firstLoad, withContent, duration, failureReason, extraAttributes) {
    var actionSubject = withContent ? 'notificationsWithContent' : 'notificationsWithoutContent';
    var buckets = withContent ? withContentBuckets : withoutContentBuckets;
    triggerOperationalEvent(createAnalyticsEvent, {
      action: 'renderFailed',
      actionSubject: actionSubject,
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        firstLoad: firstLoad,
        duration: duration,
        bucket: getRenderBucket(duration, buckets),
        failureReason: failureReason
      }, extraAttributes)
    }, analyticsContext);
  };
};
export var triggerNotificationsRenderAborted = function triggerNotificationsRenderAborted(createAnalyticsEvent, analyticsContext) {
  return function (firstLoad, withContent, duration, abortReason, extraAttributes) {
    var actionSubject = withContent ? 'notificationsWithContent' : 'notificationsWithoutContent';
    var buckets = withContent ? withContentBuckets : withoutContentBuckets;
    triggerOperationalEvent(createAnalyticsEvent, {
      action: 'renderAborted',
      actionSubject: actionSubject,
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        firstLoad: firstLoad,
        duration: duration,
        bucket: getRenderBucket(duration, buckets),
        abortReason: abortReason
      }, extraAttributes)
    }, analyticsContext);
  };
};
export var triggerReactionAddedInContextSucceeded = function triggerReactionAddedInContextSucceeded(createAnalyticsEvent, analyticsContext) {
  return function (name, emojiId, duration) {
    triggerOperationalEvent(createAnalyticsEvent, {
      action: 'succeeded',
      actionSubject: 'reactionAdded',
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        name: name,
        emojiId: emojiId,
        displayType: 'notification-bell-drawer-comment',
        duration: duration
      })
    }, analyticsContext);
  };
};
export var triggerReactionAddedInContextFailed = function triggerReactionAddedInContextFailed(createAnalyticsEvent, analyticsContext) {
  return function (name, emojiId, duration, reason) {
    triggerOperationalEvent(createAnalyticsEvent, {
      action: 'failed',
      actionSubject: 'reactionAdded',
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        name: name,
        emojiId: emojiId,
        displayType: 'notification-bell-drawer-comment',
        duration: duration,
        reason: reason
      })
    }, analyticsContext);
  };
};
export var triggerReactionAddedInContextAborted = function triggerReactionAddedInContextAborted(createAnalyticsEvent, analyticsContext) {
  return function (name, emojiId, duration, reason) {
    triggerOperationalEvent(createAnalyticsEvent, {
      action: 'aborted',
      actionSubject: 'reactionAdded',
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        name: name,
        emojiId: emojiId,
        displayType: 'notification-bell-drawer-comment',
        duration: duration,
        reason: reason
      })
    }, analyticsContext);
  };
};
export var triggerReactionDeletedInContextSucceeded = function triggerReactionDeletedInContextSucceeded(createAnalyticsEvent, analyticsContext) {
  return function (name, emojiId, duration) {
    triggerOperationalEvent(createAnalyticsEvent, {
      action: 'succeeded',
      actionSubject: 'reactionDeleted',
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        name: name,
        emojiId: emojiId,
        displayType: 'notification-bell-drawer-comment',
        duration: duration
      })
    }, analyticsContext);
  };
};
export var triggerReactionDeletedInContextFailed = function triggerReactionDeletedInContextFailed(createAnalyticsEvent, analyticsContext) {
  return function (name, emojiId, duration, reason) {
    triggerOperationalEvent(createAnalyticsEvent, {
      action: 'failed',
      actionSubject: 'reactionDeleted',
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        name: name,
        emojiId: emojiId,
        displayType: 'notification-bell-drawer-comment',
        duration: duration,
        reason: reason
      })
    }, analyticsContext);
  };
};
export var triggerReactionDeletedInContextAborted = function triggerReactionDeletedInContextAborted(createAnalyticsEvent, analyticsContext) {
  return function (name, emojiId, duration, reason) {
    triggerOperationalEvent(createAnalyticsEvent, {
      action: 'aborted',
      actionSubject: 'reactionDeleted',
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        name: name,
        emojiId: emojiId,
        displayType: 'notification-bell-drawer-comment',
        duration: duration,
        reason: reason
      })
    }, analyticsContext);
  };
};
export var triggerReactionSummaryInContextSucceeded = function triggerReactionSummaryInContextSucceeded(createAnalyticsEvent, analyticsContext) {
  return function (name, duration) {
    triggerOperationalEvent(createAnalyticsEvent, {
      action: 'succeeded',
      actionSubject: 'reactionSummary',
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        name: name,
        displayType: 'notification-bell-drawer-comment',
        duration: duration
      })
    }, analyticsContext);
  };
};
export var triggerReactionSummaryInContextFailed = function triggerReactionSummaryInContextFailed(createAnalyticsEvent, analyticsContext) {
  return function (name, duration, reason) {
    triggerOperationalEvent(createAnalyticsEvent, {
      action: 'failed',
      actionSubject: 'reactionSummary',
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        name: name,
        displayType: 'notification-bell-drawer-comment',
        duration: duration,
        reason: reason
      })
    }, analyticsContext);
  };
};
export var triggerReactionSummaryInContextAborted = function triggerReactionSummaryInContextAborted(createAnalyticsEvent, analyticsContext) {
  return function (name, duration, reason) {
    triggerOperationalEvent(createAnalyticsEvent, {
      action: 'aborted',
      actionSubject: 'reactionSummary',
      attributes: _objectSpread(_objectSpread({}, getDefaultAttributes(analyticsContext)), {}, {
        name: name,
        displayType: 'notification-bell-drawer-comment',
        duration: duration,
        reason: reason
      })
    }, analyticsContext);
  };
};

/**
 * Misc.
 */

export var useCreateFireAnalyticsFromTrigger = function useCreateFireAnalyticsFromTrigger(createTrigger) {
  var _useContext = useContext(NotificationsStoreContext),
    analyticsContext = _useContext.analyticsContext;
  var _useAnalyticsEvents = useAnalyticsEvents(),
    createAnalyticsEvent = _useAnalyticsEvents.createAnalyticsEvent;
  var currentAnalyticsContext = analyticsContext;
  return useMemo(function () {
    return createTrigger(createAnalyticsEvent, currentAnalyticsContext);
  }, [createAnalyticsEvent, createTrigger, currentAnalyticsContext]);
};